import { FunctionComponent, useEffect, useState } from 'react'
import DashboardItem from '../DashboardItem'
import { useTranslation } from 'react-i18next'
import { Button, Form, ListGroup } from 'react-bootstrap'
import { Controller, useForm } from 'react-hook-form'
import { getAntiforgeryToken } from 'Frontend/scripts/services/general.service'
import {
  checkIfUsernameIsAvailable,
  checkIfUsernameIsValid,
} from 'Frontend/scripts/services/membershipApplication.service'
import { changeUsername, getAllUsernames } from 'Frontend/scripts/services/users.service'
import Dialog from 'Features/Dialog/Dialog'

interface IChangeUsernameForm {
  username: string
  newUsername: string
}

const ChangeUsername: FunctionComponent = () => {
  const [allUsernames, setAllUsernames] = useState<string[]>([])
  const [suggestions, setSuggestions] = useState<string[]>([])
  const [changeFailed, setChangeFailed] = useState<boolean>(false)
  const [isStatusDialogOpen, setIsStatusDialogOpen] = useState<boolean>(false)
  const [inputValue, setInputValue] = useState('')
  const [activeIndex, setActiveIndex] = useState<number>(-1)
  const { t } = useTranslation()
  const {
    register,
    control,
    handleSubmit,
    setError,
    reset,
    formState: { errors },
  } = useForm<IChangeUsernameForm>({
    defaultValues: {
      username: '',
      newUsername: '',
    },
  })

  const handleInputChange = (value: string) => {
    setInputValue(value)

    if (value.trim().length === 0) {
      setSuggestions([])
      setActiveIndex(-1)
      return
    }

    const filtered = allUsernames.filter((n) => n.toLowerCase().startsWith(value.toLowerCase()))
    setSuggestions(filtered)
    setActiveIndex(-1)
  }

  const onSubmit = async (data: IChangeUsernameForm) => {
    const token = await getAntiforgeryToken()
    if (token) {
      const isCurrentUsernameAvailble = await checkIfUsernameIsAvailable(token.csrfToken, data.username)
      if (!isCurrentUsernameAvailble) {
        const isUsernameValid = await checkIfUsernameIsValid(token.csrfToken, data.newUsername)
        if (isUsernameValid) {
          const isUsernameAvailble = await checkIfUsernameIsAvailable(token.csrfToken, data.newUsername)
          if (isUsernameAvailble) {
            changeUsername(token.csrfToken, data.username, data.newUsername).then((responseObj) => {
              {
                if (responseObj == 200) {
                  setChangeFailed(false)
                } else {
                  setChangeFailed(true)
                }
                setIsStatusDialogOpen(true)
              }
            })
          } else {
            setError('newUsername', {
              type: 'manual',
              message: t('dashboard.edit-email.error-email-not-available'),
            })
          }
        } else {
          setError('newUsername', {
            type: 'manual',
            message: t('dashboard.edit-email.error-wrong-email'),
          })
        }
      } else {
        setError('username', {
          type: 'manual',
          message: t('dashboard.edit-email.error-email-not-exist'),
        })
      }
    }
  }

  const getAllCurrentUsernames = () => {
    getAllUsernames().then((usernames) => {
      setAllUsernames(usernames)
    })
  }

  useEffect(() => {
    getAllCurrentUsernames()
  }, [])

  return (
    <DashboardItem heading={t('dashboard.edit-email.edit-email')}>
      <Form noValidate onSubmit={handleSubmit(onSubmit)} autoComplete="off" className="change-username">
        <Controller
          name="username"
          control={control}
          rules={{
            required: t('dashboard.edit-email.error-add-email'),
            maxLength: {
              value: 255,
              message: t('error-text-to-long'),
            },
          }}
          render={({ field }) => (
            <div className="position-relative">
              <Form.Label htmlFor="inputUsername">{t('dashboard.edit-email.email')}</Form.Label>
              <Form.Control
                {...field}
                type="text"
                value={inputValue}
                onChange={(e) => {
                  field.onChange(e)
                  handleInputChange(e.target.value)
                }}
                onKeyDown={(e) => {
                  if (e.key === 'ArrowDown') {
                    e.preventDefault()
                    setActiveIndex((prev) => (prev < suggestions.length - 1 ? prev + 1 : prev))
                  } else if (e.key === 'ArrowUp') {
                    e.preventDefault()
                    setActiveIndex((prev) => (prev > 0 ? prev - 1 : -1))
                  } else if (e.key === 'Enter' && activeIndex >= 0) {
                    e.preventDefault()
                    const chosen = suggestions[activeIndex]
                    field.onChange(chosen)
                    setInputValue(chosen)
                    setSuggestions([])
                    setActiveIndex(-1)
                  }
                }}
                isInvalid={!!errors.username}
                aria-autocomplete="list"
                aria-expanded={suggestions.length > 0}
                aria-controls="username-suggestions"
                aria-activedescendant={activeIndex >= 0 ? `username-option-${activeIndex}` : undefined}
              />
              <Form.Control.Feedback type="invalid">{errors.username?.message}</Form.Control.Feedback>

              {inputValue.trim().length > 0 && suggestions.length > 0 && (
                <ListGroup id="username-suggestions" role="listbox" className="change-username__suggestions">
                  {suggestions.map((suggestion, index) => (
                    <ListGroup.Item
                      key={index}
                      id={`username-option-${index}`}
                      role="option"
                      aria-selected={activeIndex === index}
                      action
                      active={activeIndex === index}
                      onMouseDown={() => {
                        field.onChange(suggestion)
                        setInputValue(suggestion)
                        setSuggestions([])
                        setActiveIndex(-1)
                      }}
                    >
                      {suggestion}
                    </ListGroup.Item>
                  ))}
                </ListGroup>
              )}
            </div>
          )}
        />

        <Form.Group>
          <Form.Label htmlFor="inputUsername">{t('dashboard.edit-email.new-email')}</Form.Label>
          <Form.Control
            id="inputUsername"
            type="text"
            {...register('newUsername', {
              required: t('dashboard.edit-email.error-add-new-email'),
              maxLength: {
                value: 255,
                message: t('error-text-to-long'),
              },
            })}
            isInvalid={!!errors.newUsername}
          />
          <Form.Control.Feedback type="invalid">{errors.newUsername?.message}</Form.Control.Feedback>
        </Form.Group>
        <Button type="submit" variant="secondary" className="mt-3">
          {t('save')}
        </Button>
      </Form>
      <Dialog
        isOpen={isStatusDialogOpen}
        verificationText={
          changeFailed
            ? t('dashboard.edit-email.error-something-went-wrong-email-not-updated')
            : t('dashboard.edit-email.confirmation-email-updated')
        }
        submitText={t('dashboard.edit-email.ok')}
        buttonVariant="secondary"
        callbackSubmit={() => {
          getAllCurrentUsernames()
          setIsStatusDialogOpen(false)
          if (!changeFailed) {
            reset()
            setInputValue('')
            setSuggestions([])
            setActiveIndex(-1)
          }
        }}
      />
    </DashboardItem>
  )
}

export default ChangeUsername
